import { useCallback } from 'react';
import { useProjectStore } from '../store/useProjectStore';
import { useNavigation } from './useNavigation';
import { TranslationFile } from '../types';
import { FilePair } from '../utils/fileMatcher';
import { useFileSystemStore } from '../store/useFileSystemStore';
import { fileSystemPersistence } from '../services/shared/fileSystemPersistence';
import { saveFileImmediately } from '../services/shared/saveFileImmediately';
import { parseFile } from '../utils/fileParser';
import { features } from '../config/appMode';
import { apiParseDocxStateless, apiParsePptxStateless, type ParseStatelessResponse } from '../services/api/docxApi';
import { extractTextXmls, buildFilteredZip, XmlTooLargeError } from '../utils/clientDocxUnzip';
import { estimateTokens } from '../utils/tokenEstimator';

/**
 * Per-file source token cap (US-17-09 P1).
 *
 * Why 300K: Audit pipeline bundles 3 versions (source + AI translation + human
 * benchmark, each ~1× source) → ~3.3× expansion → 300K source × 3.3 ≈ 990K input,
 * fits within Gemini 2.5 Pro 1M context with margin. Files larger than this would
 * hard-fail audit on context cap. QC + semantic-match have headroom (2× expansion)
 * but use same cap for consistency until per-agent limits are introduced.
 *
 * If a user needs to translate a larger document, they must split it manually
 * before upload. Phase 2 will add audit/QC/semantic-match chunking that lets
 * this cap be relaxed.
 */
const FILE_TOKEN_CAP = 300_000;

export class FileTooLargeError extends Error {
    constructor(public fileName: string, public estimatedTokens: number) {
        super(`File quá lớn — vui lòng chia nhỏ rồi tải lại`);
        this.name = 'FileTooLargeError';
    }
}

/**
 * Parse a single file: DOCX/PPTX go to backend Worker API via stateless flow
 * (client unzips first, sends only text XMLs ~1-3MB).
 * Original file buffer kept on client for later export.
 */
async function parseOneFile(file: File, projectId: string, fileId: string, extra?: { humanBenchmark?: string; currentStep?: number; estimatedCredits?: TranslationFile['estimatedCredits']; cachedResult?: ParseStatelessResponse }): Promise<TranslationFile> {
    const isDocx = file.name.toLowerCase().endsWith('.docx');
    const isPptx = file.name.toLowerCase().endsWith('.pptx');

    if (features.backendParser && (isDocx || isPptx)) {
        const format = isDocx ? 'docx' : 'pptx';

        let arrayBuffer: ArrayBuffer;
        let result: ParseStatelessResponse;

        if (extra?.cachedResult) {
            // Modal đã parse rồi — chỉ cần buffer cho originalFile, KHÔNG gọi API lần 2
            arrayBuffer = await file.arrayBuffer();
            result = extra.cachedResult;
        } else {
            // Path không qua modal (vd: handleAddFiles single-file flow)
            arrayBuffer = await file.arrayBuffer();
            // extractTextXmls throws XmlTooLargeError nếu document.xml > 15MB uncompressed
            let xmls: Record<string, string>;
            try {
                xmls = await extractTextXmls(arrayBuffer, format);
            } catch (err) {
                if (err instanceof XmlTooLargeError) {
                    throw new FileTooLargeError(file.name, 0);
                }
                throw err;
            }

            const filteredZip = await buildFilteredZip(xmls);

            result = isDocx
                ? await apiParseDocxStateless(filteredZip)
                : await apiParsePptxStateless(filteredZip);
        }

        return {
            id: fileId,
            projectId,
            fileName: file.name,
            sourceText: result.segments.join('\n'),
            humanBenchmark: extra?.humanBenchmark,
            translationStatus: 'pending',
            auditStatus: 'pending',
            currentStep: (extra?.currentStep ?? 1) as 1 | 2 | 2.5 | 3 | 4,
            createdAt: new Date(),
            originalFile: arrayBuffer,   // Keep full file on client for export
            originalFormat: format,
            skipQC: true,
            backendSegments: result.segments,
            backendParagraphs: result.paragraphs,
            backendMeta: result.meta,
            backendChartLabels: result.chartLabels,
            backendChartAiStart: result.chartAiStart,
            backendSmartLabels: result.smartLabels,
            backendSmartAiStart: result.smartAiStart,
            backendParseStructure: result.parseStructure,
            estimatedCredits: extra?.estimatedCredits,
            contentLoaded: true,   // File vừa parse — content đã trong memory, skip lazy reload disk
            // No backendFileId — stateless flow
        };
    }

    // Non-DOCX or backendParser off: client-side
    const { text, originalFormat, arrayBuffer, docxParagraphMap, pptxSlideMappings } = await parseFile(file);

    // Pre-flight token cap (US-17-09 P1) — applies to all parse paths.
    const tokenEstimate = estimateTokens(text, 'auto');
    if (tokenEstimate.estimatedTokens > FILE_TOKEN_CAP) {
        throw new FileTooLargeError(file.name, tokenEstimate.estimatedTokens);
    }

    return {
        id: fileId,
        projectId,
        fileName: file.name,
        sourceText: text,
        humanBenchmark: extra?.humanBenchmark,
        translationStatus: 'pending',
        auditStatus: 'pending',
        currentStep: (extra?.currentStep ?? 1) as 1 | 2 | 2.5 | 3 | 4,
        createdAt: new Date(),
        originalFile: arrayBuffer,
        originalFormat,
        docxParagraphMap,
        pptxSlideMappings,
        skipQC: true,
        estimatedCredits: extra?.estimatedCredits,
        contentLoaded: true,   // File vừa parse — content đã trong memory
    };
}

export const useFileActions = () => {
    const {
        addFiles,
        deleteFile,
        updateFile,
        selectProject,
        selectFile,
        selectedProjectId,
        projects
    } = useProjectStore();

    const { isConnected, rootHandle } = useFileSystemStore();
    const { handleBackToProjects, handleBackToProject } = useNavigation();

    const handleAddFiles = useCallback(async (inputFiles: File[]) => {
        if (!selectedProjectId) return;

        const project = projects.find(p => p.id === selectedProjectId);
        const newFiles: TranslationFile[] = [];
        const rejectedMessages: string[] = [];
        for (const file of inputFiles) {
            const fileId = crypto.randomUUID();
            try {
                const newFile = await parseOneFile(file, selectedProjectId, fileId);
                newFiles.push(newFile);
            } catch (err) {
                if (err instanceof FileTooLargeError) {
                    rejectedMessages.push(err.message);
                    continue;
                }
                throw err;
            }
        }
        if (rejectedMessages.length > 0) {
            alert(rejectedMessages.join('\n\n'));
        }
        if (newFiles.length === 0) return;
        addFiles(newFiles);

        if (isConnected && rootHandle && project) {
            for (const file of newFiles) {
                await saveFileImmediately(rootHandle, project, file);
            }
        }
    }, [addFiles, selectedProjectId, isConnected, rootHandle, projects]);

    const handleBatchImport = useCallback(async (pairs: FilePair[]) => {
        if (!selectedProjectId) return;

        const project = projects.find(p => p.id === selectedProjectId);
        const newFiles: TranslationFile[] = [];
        const rejectedMessages: string[] = [];
        for (const pair of pairs) {
            if (!pair.source) continue;
            // Reuse benchmark text từ modal nếu có (train mode big DOCX → tránh DOMParser freeze).
            // Fallback: parse lại nếu pair không qua modal hoặc benchmark là format khác.
            const benchmarkText = pair.benchmarkText
                ?? (pair.benchmark ? (await parseFile(pair.benchmark.file)).text : '');
            const fileId = crypto.randomUUID();
            try {
                const newFile = await parseOneFile(pair.source.file, selectedProjectId, fileId, {
                    humanBenchmark: benchmarkText || undefined,
                    currentStep: benchmarkText ? 2 : 1,
                    estimatedCredits: pair.estimatedCredits,
                    cachedResult: pair.parsedResult,  // Modal đã parse → skip API call thứ 2
                });
                newFiles.push(newFile);
            } catch (err) {
                if (err instanceof FileTooLargeError) {
                    rejectedMessages.push(err.message);
                    continue;
                }
                throw err;
            }
        }
        if (rejectedMessages.length > 0) {
            alert(rejectedMessages.join('\n\n'));
        }
        if (newFiles.length === 0) return;
        addFiles(newFiles);

        if (isConnected && rootHandle && project) {
            for (const file of newFiles) {
                await saveFileImmediately(rootHandle, project, file);
            }
        }
    }, [addFiles, selectedProjectId, isConnected, rootHandle, projects]);

    const handleDeleteFile = useCallback((fileId: string) => {
        const { files } = useProjectStore.getState();
        const file = files.find(f => f.id === fileId);
        const projectId = file?.projectId;

        deleteFile(fileId);

        if (projectId) {
            const rootHandle = useFileSystemStore.getState().rootHandle;
            if (rootHandle) {
                (fileSystemPersistence as any).handle = rootHandle;
                fileSystemPersistence.deleteFileFromDisk(projectId, fileId, file?.fileName)
                    .then(() => console.log('[DeleteFile] Deleted from disk:', file?.fileName))
                    .catch(err => console.error('[DeleteFile] Disk delete failed:', err));
            }
        }
    }, [deleteFile]);

    const handleUpdateFile = useCallback((fileId: string, updates: Partial<TranslationFile>) => {
        updateFile(fileId, updates);
    }, [updateFile]);

    const handleBackToProjectsLocal = useCallback(() => {
        selectProject(null);
        handleBackToProjects();
    }, [selectProject, handleBackToProjects]);

    const handleBackToProjectLocal = useCallback(() => {
        selectFile(null);
        handleBackToProject();
    }, [selectFile, handleBackToProject]);

    return {
        handleAddFiles,
        handleBatchImport,
        handleDeleteFile,
        handleUpdateFile,
        handleBackToProjectsLocal,
        handleBackToProjectLocal
    };
};
